import { Injectable, Injector, inject } from '@angular/core';

import { isNumber } from '../../functions';
import {
  DEFAULT_DATE_PATTERN,
  EXTRA_SHORT_DATE_PATTERN,
  HEBREW_DATE_PATTERN,
  ISO_DATE_PATTERN,
  ISO_TIME_PATTERN,
  SHORT_DATE_PATTERN
} from '../constants/date.patterns';
import { IDateConfig } from '../model';
import {
  TAutoDayOptions,
  TDateDiff,
  TDateDiffUnit,
  TDateFormat,
  TDateRange,
  TDiffOptions,
  TPeriodDiffOptions,
  TTimeFormat
} from '../model/date.types';
import { DATE_CONFIG } from '../tokens';

@Injectable({
  providedIn: 'root',
  useFactory: dateServiceFactory
})
export class DateUtil {
  static dateOptions: IDateConfig;
  static dateFormatter: any;

  /**
   * @description
   * assert weather or not a date value is lower then a given date AND lower then a given date
   * @param date the date to compare against min & max dates
   * @param { min, max } the minimum and maximum values of the dates range
   * @returns a boolean indicating weather the specified date is lower then max AND higher then min
   */
  static isDateInRange = (date: Date | string, { min, max, baseYear }: TDateRange): boolean => {
    try {
      const dateValue = DateUtil.stringToDate(date, baseYear);
      const minDateValue = DateUtil.stringToDate(min);
      const maxDateValue = DateUtil.stringToDate(max);
      const inRange = dateValue >= minDateValue && dateValue <= maxDateValue;
      return inRange;
    } catch (e) {
      return false;
    }
  };

  private static isStringDate(value: string | Date): value is string {
    return typeof value === 'string';
  }

  private static isNativeDate(value: string | Date): value is Date {
    return value instanceof Date;
  }

  private static stringDateForDiff(date: Date | string, autoDayOptions: TAutoDayOptions = 'first') {
    if (DateUtil.isNativeDate(date)) {
      return DateUtil.adjustDate(date, autoDayOptions);
    }

    const requiresAutoDay = EXTRA_SHORT_DATE_PATTERN.test(date);

    if (!requiresAutoDay) {
      DateUtil.stringToDate(date);
    }

    if (autoDayOptions === 'first') {
      return DateUtil.stringToDate(`01/${date}`);
    }

    if (autoDayOptions === 'today') {
      return DateUtil.stringToDate(`${DateUtil.getDay()}/${date}`);
    }

    throw new Error(`Unable to determine auto day value when converting short date string format to date object!`);
  }

  /**
   * @description - Modifies a given Date to align with the specified day of the month.
   * @param date - The date to be adjusted.
   * @param autoDayOptions - The adjustment option ('first', 'last', or 'today').
   * @returns - The adjusted date based on the `autoDayOptions`.
   *
   * examples:
   * adjustDate("Thu Dec 05 2024 07:37:06 GMT+0200", "first") =>"Sun Dec 01 2024 00:00:00 GMT+0200"
   * adjustDate("Thu Dec 05 2024 07:37:06 GMT+0200", "last") => "Tue Dec 31 2024 00:00:00 GMT+0200"
   */

  private static adjustDate(date: Date, autoDayOptions: TAutoDayOptions): Date {
    switch (autoDayOptions) {
      case 'first':
        return new Date(date.getFullYear(), date.getMonth(), 1);
      case 'last':
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
      case 'today':
        return date;
      default:
        throw new Error(`Invalid autoDayOptions value: ${autoDayOptions}`);
    }
  }

  /**
   * @description
   * create a date value from a specified period of time which is added or subtracted to/from a specific date.
   * the period units is predefined and can be 'years' | 'months | 'weeks | 'days
   * @param date the date to calculate time passed since
   * @param { value, unit } a destruction of diff arguments
   * @returns a string formatted date which value represents the period elapsed since the original date
   * plus or minus the diff period calculation
   *
   * examples:
   * dateDiff('01/01/2015', { 10, 'days' }) => 11/01/2015
   * dateDiff('30/12/2021', { 2, 'months' }) => 28/02/2022
   */
  static dateDiff = (
    { value, unit }: TDateDiff,
    diffOptions: TDiffOptions = { anchorDate: new Date(), autoDay: 'first' }
  ): string => {
    const { anchorDate, autoDay } = diffOptions;
    const originalDate = DateUtil.stringDateForDiff(anchorDate, autoDay);
    let diffDate;
    switch (unit) {
      case 'years':
        diffDate = new Date(originalDate);
        diffDate.setFullYear(diffDate.getFullYear() + value);
        break;
      case 'months':
        diffDate = new Date(originalDate);
        diffDate.setMonth(diffDate.getMonth() + value);
        break;
      case 'weeks':
        diffDate = new Date(originalDate);
        diffDate.setDate(originalDate.getDate() + value * 7);
        break;
      case 'days':
        diffDate = new Date(originalDate);
        diffDate.setDate(diffDate.getDate() + value);
        break;
      default:
        throw new Error('Invalid period type');
    }

    return DateUtil.dateToString(diffDate, 'DD/MM/YYYY');
  };

  /**
   * @description
   * create a period diff value from a specified range of two dates (to/from). the period units
   * can be 'years' | 'months | 'weeks | 'days
   * @param startDate the start period date
   * @param endDate the end period date
   * @param unit the unit of the diff period result in which should be returned ('years' | 'months | 'weeks | 'days)
   * @returns a number which represents the time period between the two dates
   *
   * example:
   * periodDiff('01/01/2015', '20/10/2019', 'years') => 5.38
   * periodDiff('10/09/2023', '31/12/2023', 'days') => 113.04
   */

  static periodDiff(
    startDate: string | Date = new Date(),
    endDate: string | Date = new Date(),
    unit: TDateDiffUnit,
    options: TPeriodDiffOptions = { round: false, decimals: 0 }
  ): TDateDiff {
    const start = DateUtil.stringToDate(startDate);
    const end = DateUtil.stringToDate(endDate);

    const timeDiff = Math.round(Number(end.getTime()) - Number(start.getTime()));
    const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
    const avgDaysInMonth = 30.436875;
    let rawPeriod: number;

    switch (unit) {
      case 'years':
        rawPeriod = daysDiff / avgDaysInMonth / 12;
        break;
      case 'months':
        rawPeriod = daysDiff / avgDaysInMonth;
        break;
      case 'weeks':
        rawPeriod = daysDiff / 7;
        break;
      case 'days':
        rawPeriod = daysDiff;
        break;
      default:
        throw new Error('Invalid period type');
    }

    if (isNumber(options?.decimals)) {
      rawPeriod = DateUtil.truncateNumber(rawPeriod, options?.decimals);
    }

    if (options?.round) {
      rawPeriod = Math.round(rawPeriod);
    }

    return { unit, value: rawPeriod };
  }

  static truncateNumber(value: number, toDigits: number): number {
    const factor = Math.pow(10, toDigits);
    return Math.floor(value * factor) / factor;
  }

  static formatTime(value: string | Date, format: TTimeFormat = 'HH:MM') {
    const dateObject = DateUtil.stringToDate(value);
    return DateUtil.timeToString(dateObject, format);
  }

  static formatDate(value: string | Date, format: TDateFormat = 'DD/MM/YYYY') {
    const dateObject = DateUtil.stringToDate(value);
    return DateUtil.dateToString(dateObject, format);
  }

  /**
   * @description
   * converts a formatted date  string into a js Date object. this is useful when a Date
   * object is required to perform manipulation & calculations on date values
   * @param date a js Date object
   * @param format required format for result string
   * @returns a string value matching the Date object arg value
   */
  static dateToString = (date: string | Date, format: TDateFormat): string => {
    if (typeof date === 'string') {
      return date;
    }

    if (!(date instanceof Date)) {
      throw new Error('Could not complete dateToString(), "date" arg is not an instance of a Date() object');
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
    const year = date.getFullYear();
    const shortYear = year.toString().slice(-2);

    if (format === 'DD/MM/YYYY') {
      return `${day}/${month}/${year}`;
    }
    if (format === 'MM/YYYY') {
      return `${month}/${year}`;
    }
    if (format === 'MM/YY') {
      return `${month}/${shortYear}`;
    }
    if (format === 'DD/MM') {
      return `${day}/${month}`;
    }
    if (format === 'YYYY-MM-DDTHH:MM:SS') {
      return `${year}-${month}-${year}T00:00:00`;
    }
    if (format === 'YYYY-MM-DD') {
      return `${year}-${month}-${year}`;
    }
    throw new Error(
      'Could not complete dateToString(), "${format}" is not a supported date format (use:  "DD/MM/YYYY" | "MM/YYYY" | "YYYY-MM-DD" )'
    );
  };

  static timeToString = (time: string | Date, format: TTimeFormat): string => {
    if (typeof time === 'string') {
      return time;
    }

    if (!(time instanceof Date)) {
      throw new Error('Could not complete timeToString(), "time" arg is not an instance of a Date() object');
    }

    const hours = time.getHours().toString().padStart(2, '0'); // 0
    const minutes = time.getMinutes().toString().padStart(2, '0'); // 0
    const seconds = time.getSeconds().toString().padStart(2, '0'); // 0

    if (format === 'HH:MM') {
      return `${hours}:${minutes}`;
    }
    if (format === 'HH:MM:SS') {
      return `${hours}:${minutes}:${seconds}`;
    }

    throw new Error(
      'Could not complete timeToString(), "${format}" is not a supported time format (use:  "HH:MM" | "HH:MM:SS" )'
    );
  };

  private static dateFromTimeString(time: string): Date {
    const defDate = new Date();
    const timeParts = time.split(':');
    const hours = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);
    const seconds = parseInt(timeParts[2]);
    defDate.setHours(hours);
    defDate.setMinutes(minutes);
    defDate.setSeconds(seconds);
    return defDate;
  }

  /**
   * @description
   * converts a formatted date string into a js Date object. this is useful when a Date
   * object is required to perform manipulation & calculations on date values
   * @param date a string or Date object. (if date is Date object it is returned as is)
   * @returns a Date object set to a value matching the date string arg
   */
  static stringToDate = (date: string | Date, baseYear: number = 0): Date => {
    if (date instanceof Date) {
      return date;
    }

    if (!(typeof date === 'string')) {
      throw new Error('Could not complete stringToDate(), "${date}" arg is not a string');
    }

    if (ISO_TIME_PATTERN.test(date)) {
      const dateValue = DateUtil.dateFromTimeString(date);
      return dateValue;
    }
    if (ISO_DATE_PATTERN.test(date)) {
      const timestamp = Date.parse(date);
      return new Date(timestamp);
    }
    if (/** DD/MM/YYYY */ HEBREW_DATE_PATTERN.test(date)) {
      const [day, month, year] = date.split('/').map((x) => parseInt(x));
      return new Date(year, month - 1, day);
    }
    if (/** MM/YYYY */ SHORT_DATE_PATTERN.test(date)) {
      const [month, year] = date.split('/').map((x) => parseInt(x));
      return new Date(year, month - 1);
    }
    if (/** MM/YY */ EXTRA_SHORT_DATE_PATTERN.test(date)) {
      const [month, year] = date.split('/').map((x) => parseInt(x));
      return new Date(year + baseYear, month - 1);
    }

    if (DEFAULT_DATE_PATTERN.test(date)) {
      const [year, month, day] = date.split('-').map((x) => parseInt(x));
      return new Date(year, month - 1, day);
    }

    throw new Error(
      'Could not complete dateToString(), "${date}" value is not a of a supported format (use:  "DD/MM/YYYY" | "MM/YYYY" | "YYYY-MM-DD" )'
    );
  };

  static getToday = (format: TDateFormat = 'DD/MM/YYYY'): string => {
    return DateUtil.dateToString(new Date(), format);
  };

  static getYear = (): number => {
    return new Date().getFullYear();
  };

  static getMonth = (): number => {
    return new Date().getMonth() - 1;
  };

  static getDay = (): number => {
    return new Date().getDay();
  };
}

export function dateServiceFactory(injector: Injector) {
  const service = new DateUtil();
  DateUtil.dateOptions = inject(DATE_CONFIG);
  const { locale, format } = inject(DATE_CONFIG);
  DateUtil.dateFormatter = new Intl.DateTimeFormat(locale);

  return service;
}
